import { useMemo } from 'react';
import Decimal from 'decimal.js-light';
import { safeDecimal } from '@/helpers/number';
import { useGetPreAllocateInfo } from '@/query/queries/investing/useGetPreAllocateInfo';

/* Hook that used to retrieve status BEFORE confirmed allocation */
export const useAllocationStatus = () => {
  const { data: preAllocateInfo, isLoading } = useGetPreAllocateInfo();

  const accounts = useMemo(
    () => preAllocateInfo?.accounts || [],
    [preAllocateInfo],
  );

  const accountsByGroup = useMemo(() => {
    return accounts.reduce((res, acc) => {
      res[acc.group] = acc;
      return res;
    }, {});
  }, [accounts]);

  const availableCash = safeDecimal(accountsByGroup.cash?.amount);
  const totalAllocated = safeDecimal(preAllocateInfo?.total_allocated);
  const bonusAmount = safeDecimal(preAllocateInfo?.bonus_amount);
  const allocatedBonusAmount = safeDecimal(
    preAllocateInfo?.allocated_bonus_amount,
  );
  const totalBonusAmount = bonusAmount.add(allocatedBonusAmount);
  const availableCashWithoutBonus = availableCash.minus(bonusAmount).toNumber();
  const allocatedGroupA = safeDecimal(accountsByGroup.conservative?.amount);
  const allocatedGroupB = safeDecimal(accountsByGroup.moderate?.amount);
  const noCash = availableCashWithoutBonus < 0.1;
  const hasConfirmed = !!preAllocateInfo?.has_confirmed;

  const usedBonusInGroupA = allocatedGroupA.lessThan(bonusAmount)
    ? allocatedGroupA.toNumber()
    : bonusAmount.toNumber();

  const bonusRemaining = bonusAmount.minus(usedBonusInGroupA).greaterThan(0)
    ? bonusAmount.minus(usedBonusInGroupA).toNumber()
    : 0;

  const remainingAmountGroupA = safeDecimal(
    accountsByGroup.conservative?.remaining_amount,
  );
  const remainingAmountGroupB = safeDecimal(
    accountsByGroup.moderate?.remaining_amount,
  );
  const personalRemainingAmountGroupA = safeDecimal(
    accountsByGroup.conservative?.personal_remaining_amount ??
      accountsByGroup.conservative?.remaining_amount,
  );
  const personalRemainingAmountGroupB = safeDecimal(
    accountsByGroup.moderate?.personal_remaining_amount ??
      accountsByGroup.moderate?.remaining_amount,
  );
  const hasNewBrandsWithCapacityGroupA =
    accountsByGroup.conservative?.has_new_brands_with_capacity || false;
  const hasNewBrandsWithCapacityGroupB =
    accountsByGroup.moderate?.has_new_brands_with_capacity || false;
  const totalRemainingAmountGroupA = safeDecimal(
    accountsByGroup.conservative?.remaining_amount,
  );
  const totalRemainingAmountGroupB = safeDecimal(
    accountsByGroup.moderate?.total_remaining_amount,
  );

  const totalCash = preAllocateInfo
    ? totalAllocated.plus(availableCash).toNumber()
    : 0;

  const totalCashWithoutBonus = new Decimal(totalCash)
    .minus(bonusAmount)
    .toNumber();

  return {
    preAllocateInfo,
    accounts,
    accountsByGroup,
    noCash,
    availableCash: availableCash.toNumber(),
    availableCashWithoutBonus,
    totalAllocated: totalAllocated.toNumber(),
    bonusAmount: bonusAmount.toNumber(),
    allocatedBonusAmount: allocatedBonusAmount.toNumber(),
    totalBonusAmount: totalBonusAmount.toNumber(),
    allocatedGroupA: allocatedGroupA.toNumber(),
    allocatedGroupB: allocatedGroupB.toNumber(),
    remainingAmountGroupA: remainingAmountGroupA.toNumber(),
    remainingAmountGroupB: remainingAmountGroupB.toNumber(),
    personalRemainingAmountGroupA: personalRemainingAmountGroupA.toNumber(),
    personalRemainingAmountGroupB: personalRemainingAmountGroupB.toNumber(),
    hasNewBrandsWithCapacityGroupA,
    hasNewBrandsWithCapacityGroupB,
    totalRemainingAmountGroupA: totalRemainingAmountGroupA.toNumber(),
    totalRemainingAmountGroupB: totalRemainingAmountGroupB.toNumber(),
    bonusRemaining,
    usedBonusInGroupA,
    totalCash,
    totalCashWithoutBonus,
    hasConfirmed,
    isLoading,
  };
};
